"use client";

import Link from "next/link";
import { ArrowRight, Cpu, ShieldCheck, Activity, Zap, Globe } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  platformStats, platformChildren, platformFeatures, platformUseCases,
  platformWhy, platformFaq, platformCta, platformRadar, platformComparison,
} from "@/lib/data/platform-hub";

/* ============================================================
   BESPOKE 1 — Architecture signature diagram
   A central X2 core surrounded by 9 engine nodes (proprietary AI)
   with concentric platform coverage rings. Animates on load.
   ============================================================ */
function PlatformArchitectureDiagram() {
  const engines = [
    { code: "OSE", name: "Organic Simulation", color: "var(--color-cyan)" },
    { code: "NGE", name: "Narrative Generation", color: "var(--color-purple)" },
    { code: "CRE", name: "Cognitive Resonance", color: "var(--color-crimson)" },
    { code: "ECA", name: "Echo Chamber", color: "var(--color-amber)" },
    { code: "OAE", name: "Operations", color: "var(--color-emerald)" },
    { code: "DAE", name: "Data Architecture", color: "var(--color-coral)" },
    { code: "PNE", name: "Persona Networks", color: "var(--color-denim)" },
    { code: "ARE", name: "Adaptive Response", color: "var(--color-violet)" },
    { code: "CFE", name: "Content Factory", color: "var(--color-gold)" },
  ];
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="pcore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pglow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="260" cy="260" r="190" fill="url(#pglow)" />
        {[100, 145, 195, 240].map((r, i) => (
          <circle key={i} cx="260" cy="260" r={r} fill="none" stroke="rgb(255 255 255 / 0.07)" strokeWidth="0.6" strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"} />
        ))}
        {engines.map((e, i) => {
          const angle = (i / engines.length) * Math.PI * 2 - Math.PI / 2;
          const x = ROUND(260 + Math.cos(angle) * 195);
          const y = ROUND(260 + Math.sin(angle) * 195);
          return (
            <g key={e.code}>
              <line x1="260" y1="260" x2={x} y2={y} stroke={e.color} strokeOpacity="0.25" strokeWidth="0.5" strokeDasharray="2 4" />
              <circle cx={x} cy={y} r="32" fill={`color-mix(in oklch, ${e.color} 20%, transparent)`} stroke={e.color} strokeWidth="0.8" />
              <text x={x} y={y - 4} textAnchor="middle" fontSize="9" fontWeight="800" fill="white" letterSpacing="0.5">
                {e.code}
              </text>
              <text x={x} y={y + 7} textAnchor="middle" fontSize="5.5" fill="rgb(255 255 255 / 0.65)" letterSpacing="0.3">
                {e.name.toUpperCase().split(" ")[0]}
              </text>
            </g>
          );
        })}
        <g transform="translate(260, 260)">
          <circle r="65" fill="url(#pcore)" />
          <circle r="48" fill="none" stroke="rgb(255 255 255 / 0.35)" strokeWidth="1" />
          <circle r="38" fill="rgb(10 10 18 / 0.92)" />
          <text x="0" y="-2" textAnchor="middle" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">
            X2
          </text>
          <text x="0" y="14" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#dc143c" letterSpacing="2">
            PLATFORM
          </text>
          <text x="0" y="24" textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            9 ENGINES
          </text>
        </g>
      </svg>
      {/* Floating engine chips */}
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <Cpu className="h-3.5 w-3.5" style={{ color: "var(--color-crimson)" }} />
        <span className="text-xs font-semibold">9 AI Engines</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Globe className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">50+ Surfaces</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Activity className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">144-D Resonance</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <ShieldCheck className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">Zero Trust</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Performance metrics strip with animated counters,
   radial progress, and bar visualizations.
   ============================================================ */
function PerformanceMetricsStrip() {
  const metrics: Array<{
    label: string;
    to: number;
    suffix?: string;
    decimals?: number;
    accent: string;
    icon: React.ReactNode;
    progress: number;
    note: string;
  }> = [
    { label: "Narrative Penetration", to: 412, suffix: "%", accent: "var(--color-crimson)", icon: <Zap className="h-4 w-4" />, progress: 92, note: "Median improvement over organic baseline" },
    { label: "Organic Authenticity", to: 96.8, suffix: "%", decimals: 1, accent: "var(--color-emerald)", icon: <ShieldCheck className="h-4 w-4" />, progress: 97, note: "Content indistinguishable from organic" },
    { label: "Deploy-to-Live", to: 47, suffix: "m", accent: "var(--color-purple)", icon: <Cpu className="h-4 w-4" />, progress: 88, note: "Median time from sign-off to operations" },
    { label: "Uptime", to: 99.9999, suffix: "%", decimals: 4, accent: "var(--color-amber)", icon: <Activity className="h-4 w-4" />, progress: 99, note: "Equivalent to 31.5s downtime per year" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((m, i) => (
        <Reveal key={m.label} delay={i * 80}>
          <div
            className="glass rounded-2xl p-6 h-full card-glow relative overflow-hidden"
            style={{ ["--glow" as string]: m.accent }}
          >
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl opacity-25" style={{ background: m.accent }} />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <span style={{ color: m.accent }}>{m.icon}</span>
                <span className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold uppercase tracking-wider">
                  {m.label}
                </span>
              </div>
              <div className="text-4xl sm:text-5xl font-bold font-mono-num leading-none tracking-tight" style={{ color: m.accent }}>
                <Counter to={m.to} suffix={m.suffix ?? ""} decimals={m.decimals ?? 0} />
              </div>
              <div className="mt-4 h-1.5 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${m.progress}%`, background: m.accent }} />
              </div>
              <p className="mt-3 text-xs text-[rgb(var(--fg-muted))] leading-snug">{m.note}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Radar comparison: Perception X2 vs industry benchmarks
   ============================================================ */
function CapabilityRadar() {
  const data = platformRadar[0]!.axes.map((a) => ({ axis: a.axis, value: a.value }));
  return (
    <div className="relative w-full h-[360px]">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} margin={{ top: 30, right: 60, bottom: 30, left: 60 }}>
          <PolarGrid stroke="rgb(255 255 255 / 0.1)" />
          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "rgb(255 255 255 / 0.7)" }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: "rgb(255 255 255 / 0.4)" }} axisLine={false} />
          <Radar
            dataKey="value"
            stroke="var(--color-crimson)"
            fill="var(--color-crimson)"
            fillOpacity={0.35}
            strokeWidth={2}
            dot={{ r: 4, fill: "var(--color-crimson)", strokeWidth: 0 }}
          />
        </RadarChart>
      </ResponsiveContainer>
      <div className="absolute top-2 left-2 glass rounded-full px-3 py-1 text-xs font-bold" style={{ color: "var(--color-crimson)" }}>
        PERCEPTION X2
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 4 — Comparison matrix table
   ============================================================ */
function ComparisonMatrix() {
  const accentByRow = ["crimson", "purple", "cyan", "emerald", "amber", "denim", "violet", "rose"] as const;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[rgb(var(--border)/0.15)]">
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider">Dimension</th>
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--color-crimson)" }}>
              Perception X2
            </th>
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider text-[rgb(var(--fg-subtle))]">
              Traditional PR
            </th>
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider text-[rgb(var(--fg-subtle))]">
              Point Tools
            </th>
          </tr>
        </thead>
        <tbody>
          {platformComparison.map((row, i) => (
            <tr
              key={row.dimension}
              className="border-b border-[rgb(var(--border)/0.08)] hover:bg-[rgb(var(--surface)/0.4)] transition-colors"
            >
              <td className="py-4 px-4">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: `var(--color-${accentByRow[i] ?? "crimson"})` }} />
                  <span className="font-semibold text-xs">{row.dimension}</span>
                </div>
              </td>
              <td className="py-4 px-4 font-semibold" style={{ color: `var(--color-${accentByRow[i] ?? "crimson"})` }}>
                {row.perception}
              </td>
              <td className="py-4 px-4 text-[rgb(var(--fg-muted))] text-xs">{row.trad}</td>
              <td className="py-4 px-4 text-[rgb(var(--fg-muted))] text-xs">{row.point}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function PlatformHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-20 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <Reveal>
                <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                  </span>
                  <span className="eyebrow-tiny uppercase tracking-[0.28em]">Perception X2 Platform</span>
                </div>
              </Reveal>

              <div className="contents">
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    The Platform{" "}
                    <span className="bg-gradient-to-br from-[var(--color-crimson)] via-[var(--color-purple)] to-[var(--color-cyan)] bg-clip-text text-transparent">
                      Behind Perception Dominance
                    </span>
                  </h1>
                </Reveal>
              </div>

              <div className="contents">
                <Reveal delay={160}>
                  <p className="mt-6 text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    Nine proprietary AI engines. 50+ digital surfaces. 144-dimensional resonance. 300–500% narrative penetration. Zero security incidents in 15+ years.
                  </p>
                </Reveal>
              </div>

              <div className="contents">
                <Reveal delay={220}>
                  <p className="mt-4 text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    Perception X2 is the autonomous perception amplification platform engineered for organizations where narrative determines outcome. Explore the architecture, performance, and capabilities that make it the world's most advanced perception management stack.
                  </p>
                </Reveal>
              </div>

              <Reveal delay={280} className="lg:contents">
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    href="/platform/overview"
                    className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/30]"
                  >
                    Explore Platform Overview <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    href="/platform/api"
                    className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                  >
                    API Reference
                  </Link>
                </div>
              </Reveal>

              <Reveal delay={360} className="lg:contents">
                <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl">
                  {platformStats.map((s) => (
                    <div
                      key={s.label}
                      className="glass rounded-2xl p-3 card-glow"
                      style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                    >
                      <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                        <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                      </div>
                      <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={200} className="order-first lg:order-last">
                <PlatformArchitectureDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="Inside the Platform"
          title="Every Section of the Perception X2 Stack"
          description="Thirteen dedicated sections cover every facet of the platform — from architecture and capabilities to security, pricing, and roadmap."
          items={platformChildren}
          accent="crimson"
        />
      }
      features={
        <>
          <Section id="performance" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Performance Metrics"
                title="Benchmarks That Define the Category"
                description="Hard numbers across penetration, authenticity, deployment speed, and uptime — measured against the world's most demanding engagements."
                accent="cyan"
              />
              <PerformanceMetricsStrip />
            </Container>
          </Section>
          <Section id="radar" className="relative mt-8">
            <Container>
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <SectionHeading
                    eyebrow="Capability Radar"
                    title="Five-Axis Profile Across the Stack"
                    description="Coverage, authenticity, speed, insight, and resilience — each scored against the operational ceiling. The radar below shows Perception X2 against the theoretical maximum."
                    accent="crimson"
                    align="left"
                  />
                  <div className="mt-6 space-y-3">
                    {platformRadar[0]!.axes.map((a, i) => (
                      <div key={a.axis} className="flex items-center gap-3">
                        <span className="text-xs font-semibold w-24 text-[rgb(var(--fg-muted))]">{a.axis}</span>
                        <div className="flex-1 h-2 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${a.value}%`,
                              background: `linear-gradient(90deg, var(--color-crimson), var(--color-purple))`,
                            }}
                          />
                        </div>
                        <span className="text-sm font-bold font-mono-num w-12 text-right" style={{ color: "var(--color-crimson)" }}>
                          {a.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="glass rounded-2xl p-6">
                  <CapabilityRadar />
                </div>
              </div>
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Platform Features"
                title="The Capabilities That Compose the Stack"
                description="Eight foundational capabilities — each engineered to operate at machine speed, with 15+ years of operational refinement baked in."
                accent="purple"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {platformFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Platform Applications"
          title="Six Operational Patterns"
          description="The platform adapts to six core operational patterns — each tuned to the strategic posture and stakeholder dynamics of the engagement."
          cases={platformUseCases}
          accent="emerald"
        />
      }
      why={
        <>
          <Section id="comparison" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Comparative Matrix"
                title="How Perception X2 Stacks Against the Field"
                description="A direct comparison across eight dimensions that matter most to organizations deploying perception management at scale."
                accent="amber"
              />
              <div className="glass rounded-2xl p-6">
                <ComparisonMatrix />
              </div>
            </Container>
          </Section>
          <Section id="why" className="relative mt-8">
            <Container>
              <HubWhy
                eyebrow="Why Perception X2"
                title="The Differentiators That Compound"
                items={platformWhy}
                accent="cyan"
              />
            </Container>
          </Section>
        </>
      }
      faq={
        <HubFAQ
          eyebrow="Platform FAQ"
          title="Frequently Asked Questions"
          items={platformFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={platformCta.eyebrow}
          title={platformCta.title}
          description={platformCta.description}
          primary={platformCta.primary}
          secondary={platformCta.secondary}
          trustItems={platformCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
